// frontend/src/pages/customers/CustomersPage.tsx
//
// 客户管理页：客户列表 + 新建客户 Modal + 详情抽屉（客户信息 + 已存卡列表）。
// 后端：/merchant/customers、/merchant/payment-methods。卡片均为脱敏信息。

import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CreditCardOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  StarFilled,
  StarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import CustomerService, { Customer, CustomerPaymentMethod } from '../../services/customers';
import type { ListMeta } from '../../services/catalog';

const { Title, Text } = Typography;

const BRAND_LABELS: Record<string, string> = {
  visa: 'VISA',
  mastercard: 'MasterCard',
  amex: 'AMEX',
  unionpay: '银联',
  jcb: 'JCB',
  discover: 'Discover',
};

const formatBrand = (brand?: string): string => {
  if (!brand) return '银行卡';
  return BRAND_LABELS[brand.toLowerCase()] || brand.toUpperCase();
};

const formatExpiry = (pm: CustomerPaymentMethod): string => {
  if (!pm.exp_month || !pm.exp_year) return '-';
  const month = String(pm.exp_month).padStart(2, '0');
  return `${month}/${String(pm.exp_year).slice(-2)}`;
};

const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [meta, setMeta] = useState<ListMeta>({
    total: 0,
    per_page: 20,
    current_page: 1,
    last_page: 1,
  });
  const [loading, setLoading] = useState(false);

  // 新建 / 编辑 Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // 详情抽屉
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState<Customer | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<CustomerPaymentMethod[]>([]);
  const [pmLoading, setPmLoading] = useState(false);

  const loadCustomers = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    try {
      const result = await CustomerService.listCustomers(page, perPage);
      setCustomers(result.data);
      setMeta(result.meta);
    } catch (err: any) {
      message.error(err.response?.data?.message || '获取客户列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // ---- 新建 / 编辑 ----

  const openCreate = () => {
    setEditingCustomer(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    form.setFieldsValue({
      email: customer.email,
      name: customer.name,
      phone: customer.phone,
      description: customer.description,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingCustomer) {
        await CustomerService.updateCustomer(editingCustomer.id, values);
        message.success('客户已更新');
      } else {
        await CustomerService.createCustomer(values);
        message.success('客户已创建');
      }
      setModalOpen(false);
      form.resetFields();
      loadCustomers(meta.current_page, meta.per_page);
    } catch (err: any) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || '保存客户失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer: Customer) => {
    try {
      await CustomerService.deleteCustomer(customer.id);
      message.success('客户已删除');
      loadCustomers(meta.current_page, meta.per_page);
    } catch (err: any) {
      message.error(err.response?.data?.message || '删除客户失败');
    }
  };

  // ---- 详情 / 卡库 ----

  const loadPaymentMethods = useCallback(async (customerId: string) => {
    setPmLoading(true);
    try {
      const methods = await CustomerService.listPaymentMethods(customerId);
      setPaymentMethods(methods);
    } catch (err: any) {
      message.error(err.response?.data?.message || '获取已存卡列表失败');
    } finally {
      setPmLoading(false);
    }
  }, []);

  const openDetail = (customer: Customer) => {
    setCurrentCustomer(customer);
    setDrawerOpen(true);
    loadPaymentMethods(customer.id);
  };

  const handleSetDefault = async (pm: CustomerPaymentMethod) => {
    try {
      await CustomerService.setDefaultPaymentMethod(pm.id);
      message.success('已设为默认支付方式');
      if (currentCustomer) {
        loadPaymentMethods(currentCustomer.id);
        loadCustomers(meta.current_page, meta.per_page);
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || '设置默认支付方式失败');
    }
  };

  const handleDetach = async (pm: CustomerPaymentMethod) => {
    try {
      await CustomerService.detachPaymentMethod(pm.id);
      message.success('卡片已解绑');
      if (currentCustomer) {
        loadPaymentMethods(currentCustomer.id);
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || '解绑失败');
    }
  };

  // ---- 表格列 ----

  const columns: ColumnsType<Customer> = [
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => <Text copyable>{email}</Text>,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => name || <Text type="secondary">-</Text>,
    },
    {
      title: '默认卡',
      dataIndex: 'default_payment_method_id',
      key: 'default_payment_method_id',
      width: 120,
      render: (pmId: string) =>
        pmId ? (
          <Tag color="blue" icon={<CreditCardOutlined />}>
            已绑卡
          </Tag>
        ) : (
          <Tag>未绑卡</Tag>
        ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (t: string) => (t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
            详情
          </Button>
          <Button type="link" size="small" onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除该客户？"
            description="删除后其卡库信息将不可用。"
            okText="确认"
            cancelText="取消"
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            客户管理
          </Title>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadCustomers(meta.current_page, meta.per_page)}
            >
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              新建客户
            </Button>
          </Space>
        </div>

        <Table<Customer>
          rowKey="id"
          columns={columns}
          dataSource={customers || []}
          loading={loading}
          locale={{ emptyText: '暂无数据' }}
          pagination={{
            current: meta.current_page,
            pageSize: meta.per_page,
            total: meta.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => loadCustomers(page, pageSize),
          }}
        />
      </Card>

      {/* 新建 / 编辑客户 */}
      <Modal
        title={editingCustomer ? '编辑客户' : '新建客户'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={editingCustomer ? '保存' : '创建'}
        cancelText="取消"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="邮箱"
            name="email"
            rules={[
              { required: true, message: '请输入客户邮箱' },
              { type: 'email', message: '邮箱格式不正确' },
            ]}
          >
            <Input placeholder="customer@example.com" maxLength={255} />
          </Form.Item>
          <Form.Item label="姓名" name="name">
            <Input placeholder="客户姓名（可选）" maxLength={255} />
          </Form.Item>
          <Form.Item label="电话" name="phone">
            <Input placeholder="联系电话（可选）" maxLength={50} />
          </Form.Item>
          <Form.Item label="备注" name="description">
            <Input.TextArea rows={2} placeholder="备注（可选）" maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 客户详情抽屉 */}
      <Drawer
        title={
          <Space>
            <UserOutlined />
            客户详情
          </Space>
        }
        width={560}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {currentCustomer && (
          <>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label="客户 ID">
                <Text copyable>{currentCustomer.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="邮箱">{currentCustomer.email}</Descriptions.Item>
              <Descriptions.Item label="姓名">{currentCustomer.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="电话">{currentCustomer.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="备注">
                {currentCustomer.description || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {currentCustomer.created_at
                  ? dayjs(currentCustomer.created_at).format('YYYY-MM-DD HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5}>
              <CreditCardOutlined /> 已存卡
            </Title>
            <List<CustomerPaymentMethod>
              loading={pmLoading}
              dataSource={paymentMethods || []}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无已存卡" />,
              }}
              renderItem={(pm) => (
                <List.Item
                  actions={[
                    pm.is_default ? (
                      <Tag color="gold" icon={<StarFilled />} key="default">
                        默认
                      </Tag>
                    ) : (
                      <Button
                        key="set-default"
                        type="link"
                        size="small"
                        icon={<StarOutlined />}
                        onClick={() => handleSetDefault(pm)}
                      >
                        设默认
                      </Button>
                    ),
                    <Popconfirm
                      key="detach"
                      title="确认解绑该卡？"
                      okText="确认"
                      cancelText="取消"
                      onConfirm={() => handleDetach(pm)}
                    >
                      <Button type="link" size="small" danger>
                        解绑
                      </Button>
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<CreditCardOutlined style={{ fontSize: 22, color: '#2F6BFF' }} />}
                    title={
                      <Space>
                        <Text strong>{formatBrand(pm.brand)}</Text>
                        <Text>**** {pm.last4 || '----'}</Text>
                      </Space>
                    }
                    description={`有效期 ${formatExpiry(pm)} · ${pm.id}`}
                  />
                </List.Item>
              )}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default CustomersPage;
